import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Eye, FileText, Image, File, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface DocumentsViewerProps {
  documents: Record<string, any>;
  companyId: string;
}

interface DocumentPreviewProps {
  url: string;
  type: string;
}

function DocumentPreview({ url, type }: DocumentPreviewProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  if (!url || typeof url !== 'string') {
    return (
      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="aspect-[3/4] flex flex-col items-center justify-center">
          <File className="h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500 text-center">
            Invalid document URL
          </p>
        </div>
      </div>
    );
  }

  const fileExt = url.split('.').pop()?.split('?')[0]?.toLowerCase() || '';
  const isPDF = ['pdf'].includes(fileExt);
  const isImage = ['jpg', 'jpeg', 'png'].includes(fileExt);

  const getFileName = (url: string) => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const fileName = decodedUrl.split('/').pop()?.split('?')[0] || 'Document';
      return fileName;
    } catch {
      return 'Document';
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = getFileName(url);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="relative group" style={{ maxWidth: '200px' }}>
      <div className="border rounded-lg p-2 hover:shadow-md transition-shadow bg-white">
        <div className="relative aspect-[3/4] w-full bg-gray-50 rounded overflow-hidden" style={{ height: '250px' }}>
          <div className="w-full h-full flex flex-col items-center justify-center">
            {isImage ? (
              <img
                src={url}
                alt={`${type} document`}
                className="w-full h-full object-cover"
                onError={() => setError('Could not load image')}
                onLoad={() => setIsLoading(false)}
              />
            ) : isPDF ? (
              <iframe
                src={url}
                className="w-full h-full"
                title={getFileName(url)}
                style={{ minHeight: '200px' }}
              />
            ) : (
              <>
                <File className="h-16 w-16 text-gray-400 mb-2" />
                <p className="text-sm text-center text-gray-600 break-all line-clamp-2">
                  {getFileName(url)}
                </p>
              </>
            )}

            {isLoading && isImage && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}

            {/* Action Buttons */}
            <div className="absolute bottom-0 left-0 right-0 p-1 bg-white/90 border-t flex justify-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => setIsPreviewOpen(true)}
              >
                <Eye className="h-3 w-3 mr-1" />
                Preview
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={handleDownload}
              >
                <Download className="h-3 w-3 mr-1" />
                Download
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isPDF ? (
                <FileText className="h-5 w-5" />
              ) : isImage ? (
                <Image className="h-5 w-5" />
              ) : (
                <File className="h-5 w-5" />
              )}
              {getFileName(url)}
            </DialogTitle>
          </DialogHeader>
          <div className="relative flex-1 overflow-auto">
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
              {isImage ? (
                <img
                  src={url}
                  alt={`${type} document`}
                  className="max-h-[70vh] object-contain"
                />
              ) : isPDF ? (
                <div className="w-full h-full flex flex-col items-center justify-center">
                  <iframe
                    src={url}
                    className="w-full h-[70vh]"
                    title={getFileName(url)}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <File className="h-16 w-16 text-gray-400" />
                  <p className="text-sm text-gray-500">
                    Preview not available for this file type
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownload}
                  >
                    Download File
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/90">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
}

export function DocumentsViewer({ documents, companyId }: DocumentsViewerProps) {
  console.log('DocumentsViewer received documents:', documents);
  
  const documentCategories = [
    { key: 'bank_statements', label: 'Bank Statements' },
    { key: 'drivers_license', label: "Driver's License" },
    { key: 'voided_check', label: 'Voided Check' },
    { key: 'tax_returns', label: 'Tax Returns' },
    { key: 'business_license', label: 'Business License' },
    { key: 'processing_statements', label: 'Processing Statements' },
    { key: 'financial_statements', label: 'Financial Statements' },
    { key: 'lease_agreement', label: 'Lease Agreement' },
    { key: 'articles_of_incorporation', label: 'Articles of Incorporation' },
    { key: 'payarc_terms', label: 'PayArc Terms & Conditions' },
    { key: 'other', label: 'Other Documents' },
  ];

  return (
    <div className="space-y-6">
      {documentCategories.map(({ key, label }) => {
        const docs = documents[key];
        console.log(`Checking ${key}:`, docs);
        
        // Handle various document formats
        let urls: string[] = [];
        if (docs) {
          if (Array.isArray(docs)) {
            // Handle array of documents
            urls = docs
              .map((doc: any) => {
                // If it's an object with a url property
                if (doc && typeof doc === 'object' && 'url' in doc) {
                  return doc.url;
                }
                // If it's directly a string URL
                if (typeof doc === 'string') {
                  return doc;
                }
                return null;
              })
              .filter((url): url is string => typeof url === 'string' && url.length > 0);
          } else if (typeof docs === 'string' && docs.length > 0) {
            // Handle single string URL
            urls = [docs];
          } else if (docs && typeof docs === 'object' && 'url' in (docs as any)) {
            // Handle single object with url property
            urls = [(docs as any).url].filter((url): url is string => typeof url === 'string' && url.length > 0);
          }
        }

        if (urls.length === 0) return null;

        return (
          <div key={key}>
            <h3 className="text-sm font-medium mb-3">{label}</h3>
            <div className="flex flex-wrap gap-3">
              {urls.map((url: string, index: number) => (
                <DocumentPreview
                  key={`${key}-${index}`}
                  url={url}
                  type={key}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Show "No documents" message if no documents are found */}
      {documentCategories.every(({ key }) => {
        const docs = documents[key];
        if (!docs) return true;
        if (Array.isArray(docs) && docs.length === 0) return true;
        if (Array.isArray(docs)) {
          const validDocs = docs.filter((doc: any) => {
            if (typeof doc === 'string' && doc.length > 0) return true;
            if (doc && typeof doc === 'object' && 'url' in doc && doc.url) return true;
            return false;
          });
          return validDocs.length === 0;
        }
        if (typeof docs === 'string' && docs.length === 0) return true;
        return false;
      }) && (
        <div className="text-center py-8">
          <File className="h-12 w-12 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No documents uploaded</p>
        </div>
      )}
    </div>
  );
}
